/**
 * Repeatable array input — collapsible row list with sub-field inputs.
 */
import { useState } from 'react'
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { nanoid } from 'nanoid'
import type { SubFieldDefinition } from '@/features/form-builder/api/form-builder-api'
import type { InputProps } from '../input-props'
import { SubFieldInput } from './sub-field-input'

export function RepeatableArrayInput({ field, value, onChange, disabled }: InputProps) {
  const entries = (value as Array<Record<string, unknown>>) ?? []
  const subFields = (field.type_config?.sub_fields as SubFieldDefinition[] | undefined) ?? []
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const addRow = () => {
    const newEntry: Record<string, unknown> = { _entry_id: nanoid() }
    for (const sf of subFields) newEntry[sf.field_key] = null
    onChange([...entries, newEntry])
  }

  const removeRow = (idx: number) => {
    onChange(entries.filter((_, i) => i !== idx))
  }

  const setSubValue = (rowIdx: number, subKey: string, val: unknown) => {
    onChange(entries.map((e, i) => (i === rowIdx ? { ...e, [subKey]: val } : e)))
  }

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  if (subFields.length === 0) {
    return (
      <div className="bg-muted/30 border border-dashed border-border rounded-md p-4 text-sm text-muted-foreground text-center">
        فیلدهای فرعی برای این آرایه تعریف نشده
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {entries.map((entry, rowIdx) => {
        const isExpanded = expandedRows.has(rowIdx)
        const labelVal = entry[subFields[0].field_key]
        const rowLabel = labelVal ? String(labelVal) : `ردیف ${(rowIdx + 1).toLocaleString('fa-IR')}`

        return (
          <div
            key={(entry._entry_id as string) ?? rowIdx}
            className="border border-border rounded-lg overflow-hidden"
          >
            <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/20">
              <button
                type="button"
                className="flex-1 flex items-center gap-2 text-sm font-medium text-foreground text-start"
                onClick={() => toggleRow(rowIdx)}
              >
                {isExpanded
                  ? <ChevronUp   className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                {rowLabel}
              </button>
              {!disabled && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => removeRow(rowIdx)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {isExpanded && (
              <div className="px-4 py-3 space-y-3 border-t border-border">
                {subFields.map((sf) => (
                  <div key={sf.field_key} className="space-y-1">
                    <label className="text-xs font-medium text-foreground">
                      {sf.label.fa || sf.field_key}
                      {sf.is_required && <span className="text-red-400 ms-1">*</span>}
                    </label>
                    <SubFieldInput
                      subField={sf}
                      value={entry[sf.field_key]}
                      onChange={(val) => setSubValue(rowIdx, sf.field_key, val)}
                      disabled={disabled}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5 border-dashed"
          onClick={addRow}
        >
          <Plus className="w-3.5 h-3.5" />
          افزودن ردیف
        </Button>
      )}

      {entries.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-2">هنوز ردیفی اضافه نشده</p>
      )}
    </div>
  )
}
